import type { DimensionValue, StyleProp, ViewStyle } from 'react-native'
import React from 'react'
import { View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'

import { AppScreenTopBar } from '../../../components/AppScreenTopBar'

function useSkeletonAnimatedStyle() {
  const opacity = useDerivedValue(() =>
    withRepeat(
      withSequence(
        withTiming(0.7, { duration: 1000 }),
        withTiming(0.3, { duration: 1000 }),
      ),
      -1,
      true,
    ),
  )

  return useAnimatedStyle(() => ({
    opacity: opacity.value,
  }))
}

const SkeletonItem = ({
  width: itemWidth,
  height,
  style,
}: {
  width: DimensionValue
  height: number
  style?: StyleProp<ViewStyle>
}) => {
  const animatedStyle = useSkeletonAnimatedStyle()

  return (
    <Animated.View
      style={[
        {
          width: itemWidth,
          height,
          backgroundColor: '#E0E0E0',
          borderRadius: 8,
        },
        animatedStyle,
        style,
      ]}
    />
  )
}

export const LibrarySkeleton = () => {
  return (
    <View className="flex-1 bg-background-primary">
      <AppScreenTopBar />

      <View className="px-6 pt-4">
        {/* Featured Section Skeleton */}
        <View className="mb-8">
          <SkeletonItem width={180} height={24} style={{ marginBottom: 16 }} />
          <View className="bg-background-secondary p-4 rounded-xl mb-3 flex-row items-center border border-gray-50 h-24">
            <SkeletonItem
              width={48}
              height={48}
              style={{ marginRight: 16, borderRadius: 24 }}
            />
            <View className="flex-1">
              <SkeletonItem
                width="70%"
                height={20}
                style={{ marginBottom: 8 }}
              />
              <SkeletonItem width="40%" height={16} />
            </View>
          </View>
          <View className="bg-background-secondary p-4 rounded-xl mb-3 flex-row items-center border border-gray-50 h-24">
            <SkeletonItem
              width={48}
              height={48}
              style={{ marginRight: 16, borderRadius: 24 }}
            />
            <View className="flex-1">
              <SkeletonItem
                width="60%"
                height={20}
                style={{ marginBottom: 8 }}
              />
              <SkeletonItem width="30%" height={16} />
            </View>
          </View>
        </View>

        {/* Categories Skeleton */}
        <View>
          <SkeletonItem width={150} height={24} style={{ marginBottom: 16 }} />
          {[1, 2, 3].map((key) => (
            <View
              key={key}
              className="bg-background-secondary p-4 rounded-xl mb-3 flex-row items-center border border-gray-50 h-20"
            >
              <SkeletonItem
                width={48}
                height={48}
                style={{ marginRight: 16 }}
              />
              <View className="flex-1">
                <SkeletonItem
                  width="50%"
                  height={20}
                  style={{ marginBottom: 8 }}
                />
                <SkeletonItem width="30%" height={16} />
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  )
}
